import React, { useRef } from 'react';
import { motion, useSpring, useReducedMotion } from 'framer-motion';

/**
 * HeroVials — plays the Genora 3D vials render (muted, looping) and reacts to
 * scroll + cursor for a sense of depth/movement.
 *
 *   - outer  → scroll parallax (drifts slower than the page) + subtle z-rotation
 *   - tilt   → perspective rotateX/rotateY following the cursor
 *   - <video>→ the 3D render (autoplay, loop, muted, playsInline, no audio)
 *
 * Props:
 *   videoSrc  — path to the muted/looping render (e.g. "/hero-vials.mp4")
 *   posterSrc — still image shown before the video loads AND used as the
 *               reduced-motion fallback.
 *
 * Respects prefers-reduced-motion (renders the static poster image instead).
 */
export const HeroVials = ({ videoSrc, posterSrc, alt = 'Genora Peptides Research Vials' }) => {
  const ref = useRef(null);
  const videoRef = useRef(null);
  const reduce = useReducedMotion();

  // React doesn't reliably apply the `muted` attribute to the DOM property,
  // which makes browsers block muted-autoplay. Force it via the ref, then
  // kick off playback (autoplay of a muted, inline video is allowed).
  React.useEffect(() => {
    const v = videoRef.current;
    if (!v || reduce) return;
    v.muted = true;
    v.defaultMuted = true;
    const tryPlay = () => v.play().catch(() => {});
    tryPlay();
    v.addEventListener('canplay', tryPlay, { once: true });
    return () => v.removeEventListener('canplay', tryPlay);
  }, [reduce]);

  // Scroll parallax intentionally removed — the render stays static while the
  // page scrolls (per user). Cursor 3D tilt is kept for subtle depth.
  // Cursor 3D tilt (springed for smoothness).
  const tiltX = useSpring(0, { stiffness: 150, damping: 18 });
  const tiltY = useSpring(0, { stiffness: 150, damping: 18 });

  const handleMouse = (e) => {
    if (reduce) return;
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    const px = (e.clientX - rect.left) / rect.width - 0.5;
    const py = (e.clientY - rect.top) / rect.height - 0.5;
    tiltY.set(px * 10); // rotateY
    tiltX.set(-py * 7); // rotateX
  };
  const resetTilt = () => {
    tiltX.set(0);
    tiltY.set(0);
  };

  if (reduce) {
    return <img src={posterSrc} alt={alt} className="w-full h-auto max-h-[90%] object-contain" />;
  }

  return (
    <motion.div
      ref={ref}
      onMouseMove={handleMouse}
      onMouseLeave={resetTilt}
      className="relative w-full flex items-center justify-center"
      style={{ perspective: 1200 }}
    >
      {/* gentle fade + zoom-in on mount */}
      <motion.div
        className="w-full will-change-transform"
        initial={{ opacity: 0, scale: 0.92, y: 24 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
      >
        {/* cursor 3D tilt */}
        <motion.div className="w-full" style={{ rotateX: tiltX, rotateY: tiltY, transformStyle: 'preserve-3d' }}>
          <video
            ref={videoRef}
            src={videoSrc}
            poster={posterSrc}
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            aria-label={alt}
            className="w-full h-auto max-h-[90%] object-contain"
            style={{
              filter: 'drop-shadow(0 22px 28px rgba(15,23,42,0.12))',
              // Feather the hard square edges so the render dissolves into the
              // hero background instead of looking like a pasted-on block.
              maskImage:
                'radial-gradient(ellipse 78% 82% at 52% 50%, #000 55%, rgba(0,0,0,0.4) 78%, transparent 100%)',
              WebkitMaskImage:
                'radial-gradient(ellipse 78% 82% at 52% 50%, #000 55%, rgba(0,0,0,0.4) 78%, transparent 100%)',
            }}
          />
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

export default HeroVials;
